'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

export default function AdminLoginPage() {
  const router = useRouter();
  const supabase = createClient();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data, error: signInError } = await supabase.auth.signInWithPassword({ email, password });
      if (signInError) throw signInError;

      // Verify admin access
      const { data: adminData, error: adminError } = await supabase
        .from('admin_users')
        .select('id, role, is_active')
        .eq('id', data.user?.id)
        .eq('is_active', true)
        .maybeSingle();

      if (adminError || !adminData) {
        await supabase.auth.signOut();
        throw new Error('Accès refusé. Ce compte n\'a pas les droits administrateur.');
      }

      router.push('/admin/dashboard');
    } catch (err: any) {
      setError(err?.message || 'Identifiants incorrects. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-40">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2.5">
            <AppLogo size={32} />
            <span className="font-bold text-foreground text-base">WBOSOrga</span>
          </Link>
          <Link
            href="/"
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors px-3 py-1.5 rounded-lg hover:bg-muted"
          >
            <Icon name="ArrowLeftIcon" size={14} variant="outline" className="text-muted-foreground" />
            Retour à l&apos;accueil
          </Link>
        </div>
      </header>

      {/* Main */}
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md animate-slide-up">
          {/* Title */}
          <div className="text-center mb-8">
            <div className="w-14 h-14 bg-danger/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Icon name="ShieldCheckIcon" size={28} variant="outline" className="text-danger" />
            </div>
            <h1 className="text-2xl font-extrabold text-foreground mb-2">Administration WB-OS</h1>
            <p className="text-sm text-muted-foreground">
              Accès réservé aux administrateurs de la plateforme
            </p>
          </div>

          {/* Form */}
          <div className="bg-card rounded-2xl border border-border p-6 card-shadow space-y-5">
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="bg-danger/10 border border-danger/20 rounded-xl p-3 flex items-start gap-2">
                  <Icon name="ExclamationCircleIcon" size={16} variant="solid" className="text-danger flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-danger">{error}</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">
                  Adresse e-mail administrateur
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="admin@wbosorga.com"
                  className="w-full bg-input border border-border rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-danger/30 focus:border-danger transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">
                  Mot de passe
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder="••••••••"
                    className="w-full bg-input border border-border rounded-xl px-4 py-3 pr-11 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-danger/30 focus:border-danger transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Icon
                      name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'}
                      size={18}
                      variant="outline"
                      className="text-muted-foreground"
                    />
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 bg-danger text-white font-semibold text-sm px-6 py-3.5 rounded-xl hover:bg-danger/90 active:scale-95 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Vérification en cours…
                  </>
                ) : (
                  <>
                    <Icon name="LockClosedIcon" size={18} variant="outline" className="text-white" />
                    Accéder au panneau admin
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Demo credentials */}
          <div className="mt-5 bg-info-bg border border-info/20 rounded-xl p-4">
            <p className="text-xs font-bold text-info mb-2 flex items-center gap-1.5">
              <Icon name="InformationCircleIcon" size={14} variant="solid" className="text-info" />
              Compte administrateur de démonstration
            </p>
            <div className="space-y-1 text-xs text-muted-foreground font-mono-data">
              <p>Email : <span className="text-foreground font-semibold">admin@wbosorga.com</span></p>
              <p>Mot de passe : <span className="text-foreground font-semibold">Admin2026!</span></p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
